import { useCallback, useContext, useMemo } from 'react'

import { getItemTitle, toRecord } from '../../../lib/helpers'
import {
  getStatePrice,
  getStatePriceWithDiscount,
} from '../../../lib/state-price'
import { type FrameBuilderPassepartoutSize } from '../../../lib/state'
import { FrameBuilderConfigurationContext } from '../../../contexts/configuration-context'
import {
  FrameBuilderCommittedStateContext,
  FrameBuilderSelectedStateContext,
} from '../../../contexts/state-context'
import { FrameBuilderStringsContext } from '../../../contexts/strings-context'
import { FrameBuilderValidationContext } from '../../../contexts/validation-context'

import ItemList from '../../utility/item-list'
import PriceBadge from '../../utility/price-badge'
import PassepartoutSizeDetails from './passepartout-size-details'

const PassepartoutSizeList = () => {
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { lastValidState, enabledItems } = useContext(
    FrameBuilderValidationContext,
  )
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const configuration = useContext(FrameBuilderConfigurationContext)

  // Calculate frame prices for comparison
  const framePrices = useMemo(
    () =>
      toRecord<number>(
        configuration.passepartout.sizes.map((size) => ({
          [size._id]: getStatePrice(
            {
              ...committedState,
              passepartout: {
                ...committedState.passepartout,
                size,
              },
            },
            configuration,
          ),
        })),
      ),
    [committedState, configuration],
  )
  const framePricesWithDiscount = useMemo(
    () =>
      toRecord<number>(
        configuration.passepartout.sizes.map((size) => ({
          [size._id]: getStatePriceWithDiscount(
            {
              ...committedState,
              passepartout: {
                ...committedState.passepartout,
                size,
              },
            },
            configuration,
          ),
        })),
      ),
    [committedState, configuration],
  )

  const selectSize = useCallback(
    (passepartoutSize: FrameBuilderPassepartoutSize) => {
      dispatchSelectedState({
        type: 'passepartout',
        size: passepartoutSize,
      })
    },
    [dispatchSelectedState],
  )
  const handleChange = useCallback(
    (id: string) => {
      const newPassepartoutSize = configuration.passepartout.sizes.find(
        (size) => size._id === id,
      )

      if (newPassepartoutSize) {
        selectSize(newPassepartoutSize)
      }
    },
    [configuration.passepartout.sizes, selectSize],
  )

  return (
    <ItemList
      defaultValue={selectedState.passepartout.size?._id}
      onChange={handleChange}
      items={configuration.passepartout.sizes.map((size) => ({
        id: size._id,
        title: getItemTitle(
          frameBuilderStrings,
          size.title ?? '',
          size.discountPercent,
        ),
        subtitle: size.subtitle || <>&nbsp;</>,
        image: size.icon,
        description: (
          <>
            {size.size > 0 && (
              <figure
                className="w-[24px] h-[24px]"
                style={{
                  backgroundColor:
                    selectedState.passepartout.color?.color?.hex ??
                    lastValidState.passepartout.color?.color?.hex,
                }}
              />
            )}
            {!!selectedState.passepartout.size &&
              selectedState.passepartout.size._id !== size._id &&
              enabledItems.passepartoutSizeIds.includes(size._id) && (
                <>
                  <PriceBadge
                    price={
                      framePricesWithDiscount[size._id] -
                      framePricesWithDiscount[
                        selectedState.passepartout.size._id
                      ]
                    }
                    showPlusSign={true}
                    className="ml-1"
                  />
                  {!!size.discountPercent && (
                    <PriceBadge
                      price={
                        framePrices[size._id] -
                        framePricesWithDiscount[
                          selectedState.passepartout.size._id
                        ]
                      }
                      showPlusSign={true}
                      disabled
                      className="ml-1"
                    />
                  )}
                </>
              )}
          </>
        ),
        disabled: !enabledItems.passepartoutSizeIds.includes(size._id),
        error: lastValidState.passepartout.size?._id === size._id,
        details: <PassepartoutSizeDetails passepartoutSizeId={size._id} />,
      }))}
    />
  )
}

export default PassepartoutSizeList
